import { route } from "@circle-react/lib/routes/define";

// Workflows settings area. Gated behind `isWorkflowsEnabled` at the
// route-registration level; declared here unconditionally because route
// declarations are just schema.
//
// Both `list` and `history` are tabbed UIs whose active tab is driven by the
// `workflow_type` query param. Both also support Ransack-style search on
// the list page via `q[name_i_cont]`.
export const workflowsRoutes = {
  list: route("/settings/workflows", {
    title: "Workflows",
    requires: "admin",
    description:
      "Create and manage automation workflows. Tabbed by workflow type (dynamic / bulk action / scheduled / archived) and filterable by status. Name search uses a Ransack predicate.",
    queryParams: {
      status: {
        type: "string",
        description:
          "Filter by workflow status. Allowed values: all, active, inactive, draft, published, archived, in_progress.",
      },
      workflow_type: {
        type: "string",
        description:
          "Active tab on the workflows list. Allowed values: dynamic, bulk_action, scheduled, all.",
      },
      page: {
        type: "number",
        description: "Pagination page number (1-indexed).",
      },
      q: {
        type: "object",
        description:
          "Ransack search predicates. Serialized in the URL via bracket notation (e.g. `q[name_i_cont]=foo`).",
        properties: {
          name_i_cont: {
            type: "string",
            description:
              "Case-insensitive substring match on workflow name (Ransack `*_i_cont`).",
          },
        },
      },
    },
  }),

  history: route("/settings/workflows/history", {
    title: "Workflow history",
    requires: "admin",
    description:
      "Review run history and status for workflow automations. Tabbed by workflow type (dynamic / bulk action / scheduled) and filterable by run status, workflow name, run id, and triggering member.",
    queryParams: {
      workflow_type: {
        type: "string",
        description:
          "Active tab on the history page. Allowed values: dynamic, bulk_action, scheduled.",
      },
      status: {
        type: "string",
        description:
          "Filter by run result. Allowed values: success, failed, partial_success, skipped, in_progress, duplicate_skipped, cancelled.",
      },
      workflow_name: {
        type: "string",
        description:
          "Substring match on workflow name (flat param, not Ransack — distinct from the list page's `q[name_i_cont]`).",
      },
      run_id: {
        type: "string",
        description: "Filter by a specific workflow run id.",
      },
      member_id: {
        type: "string",
        description:
          "Filter to runs triggered for a specific community member id.",
      },
      page: {
        type: "number",
        description: "Pagination page number (1-indexed).",
      },
    },
  }),

  new: route("/settings/workflows/new", {
    title: "Create workflow",
    requires: "admin",
    description:
      "Create a new workflow. This is the right destination whenever the user wants to make / add / set up / create a new workflow — including when they mention a specific category like moderation or paywalls (use the `category` query param to pre-select that template context). NOT the same as the workflows list page (`/settings/workflows`), which is for browsing existing workflows.",
    queryParams: {
      category: {
        type: "string",
        description:
          "Pre-selects the workflow template category context on the create page. Allowed values: moderation, paywalls.",
      },
    },
  }),

  view: route("/settings/workflows/view/:id", {
    title: "View workflow",
    requires: "admin",
    description:
      "View a specific workflow's overview / configuration tab. For run history of this workflow, use the `runs` route.",
    pathParams: {
      id: {
        type: "number",
        description: "Numeric identifier of the workflow.",
      },
    },
  }),

  runs: route("/settings/workflows/view/:id/runs", {
    title: "Workflow runs",
    requires: "admin",
    description:
      "View run history for a specific workflow. Lists all runs with status, timing, and member details.",
    pathParams: {
      id: {
        type: "number",
        description: "Numeric identifier of the workflow.",
      },
    },
  }),

  edit: route("/settings/workflows/edit/:id", {
    title: "Edit workflow",
    requires: "admin",
    description: "Edit a specific workflow's configuration.",
    pathParams: {
      id: {
        type: "number",
        description: "Numeric identifier of the workflow.",
      },
    },
  }),
};
